/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU128Decoder,
  getU128Encoder,
  getU32Decoder,
  getU32Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  getAddressFromResolvedInstructionAccount,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { CPMM_PROGRAM_ADDRESS } from '../programs';

export const INITIALIZE_ORACLE_DISCRIMINATOR = new Uint8Array([
  144, 223, 131, 120, 196, 253, 181, 99,
]);

export function getInitializeOracleDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    INITIALIZE_ORACLE_DISCRIMINATOR,
  );
}

export type InitializeOracleInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountConfig extends string | AccountMeta<string> = string,
  TAccountPool extends string | AccountMeta<string> = string,
  TAccountOracle extends string | AccountMeta<string> = string,
  TAccountAdmin extends string | AccountMeta<string> = string,
  TAccountPayer extends string | AccountMeta<string> = string,
  TAccountSystemProgram extends string | AccountMeta<string> =
    '11111111111111111111111111111111',
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountConfig extends string
        ? ReadonlyAccount<TAccountConfig>
        : TAccountConfig,
      TAccountPool extends string
        ? ReadonlyAccount<TAccountPool>
        : TAccountPool,
      TAccountOracle extends string
        ? WritableAccount<TAccountOracle>
        : TAccountOracle,
      TAccountAdmin extends string
        ? ReadonlySignerAccount<TAccountAdmin> &
            AccountSignerMeta<TAccountAdmin>
        : TAccountAdmin,
      TAccountPayer extends string
        ? WritableSignerAccount<TAccountPayer> &
            AccountSignerMeta<TAccountPayer>
        : TAccountPayer,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export type InitializeOracleInstructionData = {
  discriminator: ReadonlyUint8Array;
  maxPriceChangeRatioQ64: bigint;
  observationIntervalSec: number;
};

export type InitializeOracleInstructionDataArgs = {
  maxPriceChangeRatioQ64: number | bigint;
  observationIntervalSec: number;
};

export function getInitializeOracleInstructionDataEncoder(): FixedSizeEncoder<InitializeOracleInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['maxPriceChangeRatioQ64', getU128Encoder()],
      ['observationIntervalSec', getU32Encoder()],
    ]),
    (value) => ({ ...value, discriminator: INITIALIZE_ORACLE_DISCRIMINATOR }),
  );
}

export function getInitializeOracleInstructionDataDecoder(): FixedSizeDecoder<InitializeOracleInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['maxPriceChangeRatioQ64', getU128Decoder()],
    ['observationIntervalSec', getU32Decoder()],
  ]);
}

export function getInitializeOracleInstructionDataCodec(): FixedSizeCodec<
  InitializeOracleInstructionDataArgs,
  InitializeOracleInstructionData
> {
  return combineCodec(
    getInitializeOracleInstructionDataEncoder(),
    getInitializeOracleInstructionDataDecoder(),
  );
}

export type InitializeOracleAsyncInput<
  TAccountConfig extends string = string,
  TAccountPool extends string = string,
  TAccountOracle extends string = string,
  TAccountAdmin extends string = string,
  TAccountPayer extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  config: Address<TAccountConfig>;
  pool: Address<TAccountPool>;
  oracle?: Address<TAccountOracle>;
  admin: TransactionSigner<TAccountAdmin>;
  payer: TransactionSigner<TAccountPayer>;
  systemProgram?: Address<TAccountSystemProgram>;
  maxPriceChangeRatioQ64: InitializeOracleInstructionDataArgs['maxPriceChangeRatioQ64'];
  observationIntervalSec: InitializeOracleInstructionDataArgs['observationIntervalSec'];
};

export async function getInitializeOracleInstructionAsync<
  TAccountConfig extends string,
  TAccountPool extends string,
  TAccountOracle extends string,
  TAccountAdmin extends string,
  TAccountPayer extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address = typeof CPMM_PROGRAM_ADDRESS,
>(
  input: InitializeOracleAsyncInput<
    TAccountConfig,
    TAccountPool,
    TAccountOracle,
    TAccountAdmin,
    TAccountPayer,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  InitializeOracleInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountPool,
    TAccountOracle,
    TAccountAdmin,
    TAccountPayer,
    TAccountSystemProgram
  >
> {
  // Program address.
  const programAddress = config?.programAddress ?? CPMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: false },
    pool: { value: input.pool ?? null, isWritable: false },
    oracle: { value: input.oracle ?? null, isWritable: true },
    admin: { value: input.admin ?? null, isWritable: false },
    payer: { value: input.payer ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.oracle.value) {
    accounts.oracle.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(new Uint8Array([111, 114, 97, 99, 108, 101])),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount('pool', accounts.pool.value),
        ),
      ],
    });
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      '11111111111111111111111111111111' as Address<'11111111111111111111111111111111'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('config', accounts.config),
      getAccountMeta('pool', accounts.pool),
      getAccountMeta('oracle', accounts.oracle),
      getAccountMeta('admin', accounts.admin),
      getAccountMeta('payer', accounts.payer),
      getAccountMeta('systemProgram', accounts.systemProgram),
    ],
    data: getInitializeOracleInstructionDataEncoder().encode(
      args as InitializeOracleInstructionDataArgs,
    ),
    programAddress,
  } as InitializeOracleInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountPool,
    TAccountOracle,
    TAccountAdmin,
    TAccountPayer,
    TAccountSystemProgram
  >);
}

export type InitializeOracleInput<
  TAccountConfig extends string = string,
  TAccountPool extends string = string,
  TAccountOracle extends string = string,
  TAccountAdmin extends string = string,
  TAccountPayer extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  config: Address<TAccountConfig>;
  pool: Address<TAccountPool>;
  oracle: Address<TAccountOracle>;
  admin: TransactionSigner<TAccountAdmin>;
  payer: TransactionSigner<TAccountPayer>;
  systemProgram?: Address<TAccountSystemProgram>;
  maxPriceChangeRatioQ64: InitializeOracleInstructionDataArgs['maxPriceChangeRatioQ64'];
  observationIntervalSec: InitializeOracleInstructionDataArgs['observationIntervalSec'];
};

export function getInitializeOracleInstruction<
  TAccountConfig extends string,
  TAccountPool extends string,
  TAccountOracle extends string,
  TAccountAdmin extends string,
  TAccountPayer extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address = typeof CPMM_PROGRAM_ADDRESS,
>(
  input: InitializeOracleInput<
    TAccountConfig,
    TAccountPool,
    TAccountOracle,
    TAccountAdmin,
    TAccountPayer,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): InitializeOracleInstruction<
  TProgramAddress,
  TAccountConfig,
  TAccountPool,
  TAccountOracle,
  TAccountAdmin,
  TAccountPayer,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress = config?.programAddress ?? CPMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: false },
    pool: { value: input.pool ?? null, isWritable: false },
    oracle: { value: input.oracle ?? null, isWritable: true },
    admin: { value: input.admin ?? null, isWritable: false },
    payer: { value: input.payer ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      '11111111111111111111111111111111' as Address<'11111111111111111111111111111111'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('config', accounts.config),
      getAccountMeta('pool', accounts.pool),
      getAccountMeta('oracle', accounts.oracle),
      getAccountMeta('admin', accounts.admin),
      getAccountMeta('payer', accounts.payer),
      getAccountMeta('systemProgram', accounts.systemProgram),
    ],
    data: getInitializeOracleInstructionDataEncoder().encode(
      args as InitializeOracleInstructionDataArgs,
    ),
    programAddress,
  } as InitializeOracleInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountPool,
    TAccountOracle,
    TAccountAdmin,
    TAccountPayer,
    TAccountSystemProgram
  >);
}

export type ParsedInitializeOracleInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    config: TAccountMetas[0];
    pool: TAccountMetas[1];
    oracle: TAccountMetas[2];
    admin: TAccountMetas[3];
    payer: TAccountMetas[4];
    systemProgram: TAccountMetas[5];
  };
  data: InitializeOracleInstructionData;
};

export function parseInitializeOracleInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedInitializeOracleInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 6) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 6,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      config: getNextAccount(),
      pool: getNextAccount(),
      oracle: getNextAccount(),
      admin: getNextAccount(),
      payer: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getInitializeOracleInstructionDataDecoder().decode(instruction.data),
  };
}
